import { Environment } from '@cobliteam/react-environments';
import type { Camelized } from '@cobliteam/utils/camelize';
import { infiniteQueryOptions, mutationOptions, type QueryClient, queryOptions, skipToken } from '@tanstack/react-query';
import type { z } from 'zod/v4';
import type { MediaStatus } from '../__generated__/graphql';
import {
  CreateMediaDocument,
  EventsSeveritiesSummaryDocument,
  type EventsSeveritiesSummaryQuery,
  type EventsSeveritiesSummaryQueryVariables,
  GetDeviceMediaCountDocument,
  type GetDeviceMediaCountQueryVariables,
  GetMediaDocument,
  type GetMediaQuery,
  type GetMediaQueryVariables,
  GetMediaThumbnailDocument,
  GetMediaViewDocument,
  type GetMediaViewQuery,
  type GetMediaViewQueryVariables,
} from '../__generated__/graphql';
import { CreateSharedMediaResponseDTO, MediaComment as MediaCommentSchema, UnarchiveSafetyReviewResponse } from '../__generated__/rest/poolerMedia.zod';
import { requestGraphQL } from '../client/graphql';
import { requestHttp } from '../client/http';

export type { ArchivedMediaFilter, EventTrigger, MediaRequestStatus, MediaStatus, MediaType, RequestOrigin, SafetyReviewOrigin, Severity } from '../__generated__/graphql';

/** Página de Mídias como o janus devolve, em camelCase. */
export type MediaPage = NonNullable<Camelized<GetMediaQuery>['media']>;

/** Uma Mídia da lista: só os campos que o `getMedia` seleciona. */
export type MediaListItem = NonNullable<NonNullable<MediaPage['content']>[number]>;

export type MediaView = NonNullable<Camelized<GetMediaViewQuery>['mediaView']>;

/** O detalhe de um evento de vídeo, como o `getMedia` seleciona. */
export type MediaEventDetails = Extract<MediaListItem['mediaDetails'], { eventTrigger: unknown }>;

/** O detalhe de um vídeo solicitado (trecho ou resumo do trajeto), como o `getMedia` seleciona. */
export type MediaFootageDetails = Extract<MediaListItem['mediaDetails'], { duration: unknown }>;

// O `mediaDetails` é uma união, e a lista e o detalhe selecionam campos diferentes: os guards valem para as duas.
export const isEventDetails = <T extends object>(details: T | null | undefined): details is Extract<T, { eventTrigger: unknown }> => !!details && 'eventTrigger' in details;

export const isFootageDetails = <T extends object>(details: T | null | undefined): details is Extract<T, { duration: unknown }> => !!details && 'duration' in details;

type WithUrls = { eventMediaUrl?: string | null; footageMediaUrl?: string | null; eventThumbnailUrl?: string | null; footageThumbnailUrl?: string | null };

// O fio separa as URLs por tipo (`event_media_url`, `footage_media_url`) porque os mocks e o api-lib casam por esses nomes.
export const mediaUrlOf = (details: object | null | undefined): string | undefined => {
  const urls = details as WithUrls | null | undefined;
  return urls?.eventMediaUrl ?? urls?.footageMediaUrl ?? undefined;
};

export const thumbnailUrlOf = (details: object | null | undefined): string | undefined => {
  const urls = details as WithUrls | null | undefined;
  return urls?.eventThumbnailUrl ?? urls?.footageThumbnailUrl ?? undefined;
};

export type EventsSeveritiesSummary = NonNullable<EventsSeveritiesSummaryQuery['eventsSeveritiesSummary']>;

export type MediaListVariables = GetMediaQueryVariables;
export type MediaCountVariables = GetDeviceMediaCountQueryVariables;
export type EventsSeveritiesSummaryVariables = EventsSeveritiesSummaryQueryVariables;

/** O fio do janus, em snake_case: o formato que os mocks do e2e e da demo precisam seguir. */
export type MediaPageWire = NonNullable<GetMediaQuery['media']>;
export type MediaViewWire = NonNullable<GetMediaViewQuery['mediaView']>;

/** Um comentário como o pooler devolve na criação e na edição, em camelCase. */
export type MediaComment = Camelized<z.output<typeof MediaCommentSchema>>;

export type SharedMedia = Camelized<z.output<typeof CreateSharedMediaResponseDTO>>;

/** A revisão depois de desarquivar, em camelCase. */
export type UnarchivedSafetyReview = Camelized<z.output<typeof UnarchiveSafetyReviewResponse>>;

/** O fio do pooler, em snake_case, para os mocks. */
export type MediaCommentWire = z.input<typeof MediaCommentSchema>;
export type SharedMediaWire = z.input<typeof CreateSharedMediaResponseDTO>;
export type UnarchivedSafetyReviewWire = z.input<typeof UnarchiveSafetyReviewResponse>;

export type CreateMediaRequestInput = {
  vehicleId?: string;
  startTime: number;
  endTime: number;
  isTimelapse?: boolean;
  enableEmailNotification?: boolean;
  tripId?: string;
};

export type MediaCommentInput = { mediaId: string; content: string };
export type UpdateMediaCommentInput = MediaCommentInput & { commentId: string };
export type DeleteMediaCommentInput = { mediaId: string; commentId: string };
export type FavoriteMediaInput = { mediaId: string; isFavorite: boolean };

/** A página da navegação entre Mídias do detalhe: 5 antes e 5 depois da Mídia aberta, por página. */
export type BidirectionalPageParam = {
  page: number;
  totalElements: number;
  afterPages: number;
  afterCount: number;
  beforePages: number;
  beforeCount: number;
};

export type BidirectionalPage = { content: MediaListItem[] } & BidirectionalPageParam;

export type BidirectionalInput = {
  kind: 'events' | 'requests';
  /** Só entra na key: a Mídia aberta muda na navegação, e a lista não pode voltar ao zero com ela. */
  filters: MediaListVariables;
  /** A Mídia aberta, que abre a primeira página entre as de antes e as de depois. */
  anchor: MediaListItem;
  /** As variables de cada lado, com `page` na posição do api-lib: os mocks do e2e casam pela ordem das chaves. */
  before: MediaListVariables;
  after: MediaListVariables;
};

// As keys ainda são as do hook legado: o `useCameraSafety` e o `MediaRequestDialog` ainda escrevem nelas.
const ROOT = 'CAMERA-MEDIA';
const LIST_EVENT = 'LIST-EVENT';
const LIST_REQUEST = 'LIST-REQUEST';
const DETAILS_LIST = 'DETAILS-LIST';
const BIDIRECTION_EVENTS = 'BIDIRECTION-EVENTS';
const BIDIRECTION_REQUESTS = 'BIDIRECTION-REQUESTS';
const LIST_STALE_TIME = 5 * 60 * 1000;
const MANUAL_RETRY_REQUEST = 'MANUAL_RETRY_REQUEST';

const poolerHost = () => Environment.getInstance().getEnvironment().apiHost;
const BIDIRECTIONAL_PAGE_SIZE = 5;

// O janus resolve os usuários (`created_by`) no `/users` da frota, que dá 403 para quem não pode listar usuários. Quem
// chama passa `withUsers: false` para nem pedir esses campos; se o 403 vier mesmo assim, a Mídia volta sem o nome,
// como no errorPolicy 'all' do api-lib, em vez de a lista inteira falhar.
const PARTIAL_USERS = { allowPartialData: true } as const;

const fetchPage = async (variables: MediaListVariables, signal: AbortSignal): Promise<MediaPage | null> => {
  const { media } = await requestGraphQL(GetMediaDocument, variables, { signal, ...PARTIAL_USERS });
  return media ?? null;
};

// Como no api-lib: a página 0 não substitui a `page` das variables.
const withPage = (variables: MediaListVariables, pageParam: number): MediaListVariables => ({ ...variables, page: pageParam || variables.page });

const nextPage = (lastPage: MediaPage | null, pages: unknown[]) => (lastPage?.last === false ? pages.length : undefined);

type PagesData = { pages: ({ content?: (MediaListItem | null)[] | null } | null)[] };

const updateMediaInPages = (client: QueryClient, queryKey: readonly unknown[], mediaId: string, update: (media: MediaListItem) => MediaListItem) => {
  client.setQueriesData<PagesData>({ queryKey }, (old) =>
    old
      ? {
          ...old,
          pages: old.pages.map((page) => (page ? { ...page, content: page.content?.map((media) => (media?.id === mediaId ? update(media) : media)) } : page)),
        }
      : old
  );
};

const markAsWatched = (media: MediaListItem): MediaListItem => (media.mediaStatus === 'NEW' ? { ...media, mediaStatus: 'WATCHED' } : media);

// O `mediaView` conta a visualização no pooler, que passa a devolver `WATCHED`; as listas em cache refletem isso sem
// buscar de novo. O prefixo `LIST-EVENT` cobre também a lista do detalhe.
const reflectView = (client: QueryClient, mediaId: string, view: MediaView) => {
  const isEvent = view.mediaInfo?.mediaType === 'EVENT_VIDEO';
  updateMediaInPages(client, [ROOT, isEvent ? LIST_EVENT : LIST_REQUEST], mediaId, (media) => markAsWatched({ ...media, mediaViewCount: (media.mediaViewCount ?? 0) + 1 }));
  updateMediaInPages(client, [ROOT, isEvent ? BIDIRECTION_EVENTS : BIDIRECTION_REQUESTS], mediaId, markAsWatched);
};

const initialBidirectionalParam: BidirectionalPageParam = { page: 0, totalElements: 0, afterCount: 0, afterPages: 0, beforeCount: 0, beforePages: 0 };

const sameSides = (page: BidirectionalPage): Omit<BidirectionalPageParam, 'page'> => ({
  afterCount: page.afterCount,
  afterPages: page.afterPages,
  beforeCount: page.beforeCount,
  beforePages: page.beforePages,
  totalElements: page.totalElements,
});

const fetchBidirectionalPage = async ({ anchor, before, after }: BidirectionalInput, pageParam: BidirectionalPageParam, signal: AbortSignal): Promise<BidirectionalPage> => {
  const { page } = pageParam;
  // A página positiva desce no tempo, a negativa sobe, e a 0 busca os dois lados.
  const beforePage = page >= 0 ? await fetchPage({ ...before, page, size: BIDIRECTIONAL_PAGE_SIZE }, signal) : null;
  const afterPage = page <= 0 ? await fetchPage({ ...after, page: page === 0 ? 0 : -page, size: BIDIRECTIONAL_PAGE_SIZE }, signal) : null;

  const beforeContent = (beforePage?.content ?? []).filter((media): media is MediaListItem => !!media);
  const afterContent = (afterPage?.content ?? []).filter((media): media is MediaListItem => !!media);
  const content = afterContent.toReversed().concat(page === 0 ? [anchor, ...beforeContent] : beforeContent);

  if (page !== 0) return { content, ...pageParam };
  const afterCount = afterPage?.totalElements ?? 0;
  const beforeCount = beforePage?.totalElements ?? 0;
  return {
    content,
    page,
    afterCount,
    afterPages: afterPage?.totalPages ?? 0,
    beforeCount,
    beforePages: beforePage?.totalPages ?? 0,
    totalElements: afterCount + beforeCount + 1,
  };
};

export const mediaQueries = {
  all: () => [ROOT] as const,

  /** Prefixo de todos os `view`, para invalidar a Mídia aberta em qualquer tela. */
  views: () => [...mediaQueries.all(), 'VIEW'] as const,

  /** Prefixo das tabelas de Eventos e de Favoritos; também cobre as `detailsLists()`, que ficam embaixo dele. */
  eventTables: () => [...mediaQueries.all(), LIST_EVENT] as const,

  /** Prefixo das listas de eventos ao lado do detalhe. */
  detailsLists: () => [...mediaQueries.eventTables(), DETAILS_LIST] as const,

  /**
   * A Mídia com a URL assinada do vídeo. Buscar conta uma visualização, e as listas em cache passam a mostrar a
   * contagem nova e a Mídia como vista. Sem `mediaId`, a query fica desligada.
   */
  view: (mediaId?: string, shouldGenerateDescription = false, { withUsers }: Pick<GetMediaViewQueryVariables, 'withUsers'> = {}) =>
    queryOptions({
      // `withUsers` fica fora da key: segue a permissão da sessão, e o seed das evidências do coaching usa esta key.
      queryKey: [...mediaQueries.views(), mediaId, shouldGenerateDescription] as const,
      queryFn: mediaId
        ? async ({ signal, client }) => {
            const { mediaView } = await requestGraphQL(GetMediaViewDocument, { mediaId, shouldGenerateDescription, withUsers }, { signal, ...PARTIAL_USERS });
            if (mediaView) reflectView(client, mediaId, mediaView);
            return mediaView ?? null;
          }
        : skipToken,
    }),

  /** Só a thumbnail, sem os efeitos do `view`. Não muda, então fica em cache por 5 minutos. */
  thumbnail: (mediaId?: string) =>
    queryOptions({
      queryKey: [...mediaQueries.all(), 'THUMBNAIL', mediaId] as const,
      queryFn: mediaId
        ? async ({ signal }) => {
            const { mediaView } = await requestGraphQL(GetMediaThumbnailDocument, { mediaId }, { signal });
            const details = mediaView?.mediaInfo?.mediaDetails;
            if (!details) return null;
            return ('eventThumbnailUrl' in details ? details.eventThumbnailUrl : details.footageThumbnailUrl) ?? null;
          }
        : skipToken,
      staleTime: LIST_STALE_TIME,
    }),

  /** Quantas Mídias batem com as variables, sem trazer a lista. */
  count: (variables: MediaCountVariables) =>
    queryOptions({
      queryKey: [...mediaQueries.all(), 'COUNT', variables] as const,
      queryFn: async ({ signal }) => {
        const { media } = await requestGraphQL(GetDeviceMediaCountDocument, variables, { signal });
        return media?.totalElements ?? 0;
      },
      refetchOnMount: false,
    }),

  /** Uma página, de eventos ou de solicitações, pelo que as variables pedem. */
  list: (variables: MediaListVariables) =>
    queryOptions({
      queryKey: [...mediaQueries.all(), 'LIST', variables] as const,
      queryFn: ({ signal }) => fetchPage(variables, signal),
      staleTime: LIST_STALE_TIME,
    }),

  /** A tabela de eventos, página a página. */
  eventsInfinite: (variables: MediaListVariables) =>
    infiniteQueryOptions({
      queryKey: [...mediaQueries.eventTables(), variables] as const,
      queryFn: ({ pageParam, signal }) => fetchPage(withPage(variables, pageParam), signal),
      initialPageParam: 0,
      getNextPageParam: nextPage,
    }),

  /** A tabela de solicitações, página a página. */
  requestsInfinite: (variables: MediaListVariables) =>
    infiniteQueryOptions({
      queryKey: [...mediaQueries.all(), LIST_REQUEST, variables] as const,
      queryFn: ({ pageParam, signal }) => fetchPage(withPage(variables, pageParam), signal),
      initialPageParam: 0,
      getNextPageParam: nextPage,
    }),

  /** A lista de eventos ao lado do detalhe, quando ele abre com os filtros da tabela. */
  detailsInfinite: (variables: MediaListVariables) =>
    infiniteQueryOptions({
      queryKey: [...mediaQueries.detailsLists(), variables] as const,
      queryFn: ({ pageParam, signal }) => fetchPage(withPage(variables, pageParam), signal),
      initialPageParam: 0,
      getNextPageParam: nextPage,
    }),

  /** A lista ao lado do detalhe aberto por link: as Mídias antes e depois dele no tempo. Sem `input`, fica desligada. */
  bidirectional: (kind: BidirectionalInput['kind'], input?: BidirectionalInput) =>
    infiniteQueryOptions({
      queryKey: [...mediaQueries.all(), kind === 'events' ? BIDIRECTION_EVENTS : BIDIRECTION_REQUESTS, input?.filters] as const,
      queryFn: input ? ({ pageParam, signal }) => fetchBidirectionalPage(input, pageParam, signal) : skipToken,
      initialPageParam: initialBidirectionalParam,
      getNextPageParam: (lastPage) => (lastPage.page + 1 >= lastPage.beforePages ? undefined : { ...sameSides(lastPage), page: lastPage.page + 1 }),
      getPreviousPageParam: (firstPage) => (firstPage.page - 1 <= -firstPage.afterPages ? undefined : { ...sameSides(firstPage), page: firstPage.page - 1 }),
    }),

  /** Quantos eventos há em cada severidade, com os filtros da tabela. */
  severitiesSummary: (variables: EventsSeveritiesSummaryVariables) =>
    queryOptions({
      queryKey: [...mediaQueries.all(), 'EVENTS-SEVERITY-SUMMARY', variables] as const,
      queryFn: async ({ signal }) => {
        const { eventsSeveritiesSummary } = await requestGraphQL(EventsSeveritiesSummaryDocument, variables, { signal });
        return eventsSeveritiesSummary ?? null;
      },
    }),
};

type Snapshot = [readonly unknown[], unknown][];

// Cancela as buscas em voo, para não sobrescreverem a escrita otimista, e guarda o anterior para o rollback.
const snapshot = async (client: QueryClient, queryKeys: (readonly unknown[])[]): Promise<Snapshot> => {
  await Promise.all(queryKeys.map((queryKey) => client.cancelQueries({ queryKey })));
  return queryKeys.flatMap((queryKey) => client.getQueriesData({ queryKey }));
};

const restore = (client: QueryClient, previous: Snapshot | undefined) => {
  for (const [queryKey, data] of previous ?? []) client.setQueryData(queryKey, data);
};

const updateViewInfo = (client: QueryClient, mediaId: string, update: (mediaInfo: NonNullable<MediaView['mediaInfo']>) => NonNullable<MediaView['mediaInfo']>) => {
  client.setQueriesData<MediaView | null>({ queryKey: [...mediaQueries.views(), mediaId] }, (view) => (view?.mediaInfo ? { ...view, mediaInfo: update(view.mediaInfo) } : view));
};

type ViewComment = NonNullable<NonNullable<NonNullable<MediaView['mediaInfo']>['comments']>[number]>;

type ListQuery = { queryKey: readonly unknown[] };

const isDetailsList = ({ queryKey }: ListQuery) => mediaQueries.detailsLists().every((part, index) => queryKey[index] === part);

// As tabelas de Eventos, Favoritos e Arquivados; a lista ao lado do detalhe segue mostrando o vídeo, com a tag nova.
const eventTablesFilter = { queryKey: mediaQueries.eventTables(), predicate: (query: ListQuery) => !isDetailsList(query) };

const isArchivedTab = (query: ListQuery) => !isDetailsList(query) && (query.queryKey[2] as MediaListVariables | undefined)?.archived === 'ONLY';

const withoutMedia =
  (mediaId: string) =>
  (old: PagesData | undefined): PagesData | undefined =>
    old ? { ...old, pages: old.pages.map((page) => (page ? { ...page, content: page.content?.filter((media) => media?.id !== mediaId) } : page)) } : old;

// O mesmo mapa do `getMediaStatusBasedOnReviewStatus` do ui-business, que a libs/api não importa: sem revisão, o vídeo fica como visto.
const MEDIA_STATUS_OF_REVIEW: Record<NonNullable<UnarchivedSafetyReview['status']>, MediaStatus> = { RELEVANT: 'RELEVANT', DISMISSED: 'DISMISSED', ARCHIVED: 'ARCHIVED' };

const mediaStatusOf = (status: UnarchivedSafetyReview['status']): MediaStatus => (status ? MEDIA_STATUS_OF_REVIEW[status] : 'WATCHED');

// Traduz a revisão do REST para o formato do cache em vez de invalidar: refazer o `view` contaria outra visualização.
const reflectUnarchive = (client: QueryClient, mediaId: string, review: UnarchivedSafetyReview) => {
  const archiveFields = {
    status: review.status,
    archivedAt: review.archivedAt ?? null,
    archivedById: review.archivedBy ?? null,
    archivedByName: review.archivedByName ?? null,
    archivedOrigin: review.archivedOrigin ?? null,
  };
  const update = (media: MediaListItem): MediaListItem =>
    media.safetyReview ? { ...media, mediaStatus: mediaStatusOf(review.status), safetyReview: { ...media.safetyReview, ...archiveFields } } : media;
  updateMediaInPages(client, mediaQueries.detailsLists(), mediaId, update);
  updateMediaInPages(client, [ROOT, BIDIRECTION_EVENTS], mediaId, update);
  updateViewInfo(client, mediaId, (mediaInfo) =>
    mediaInfo.safetyReview ? { ...mediaInfo, mediaStatus: mediaStatusOf(review.status), safetyReview: { ...mediaInfo.safetyReview, ...archiveFields } } : mediaInfo
  );
};

type ReviewStatus = UnarchivedSafetyReview['status'];

/**
 * Reflete nas tabelas de eventos uma revisão salva pelo hook legado: o vídeo recém-arquivado sai de Eventos e Favoritos,
 * e as tabelas voltam ao servidor quando o arquivamento muda. A lista ao lado do detalhe mantém o vídeo, com a tag nova.
 */
export const reflectReviewSaved = (client: QueryClient, { mediaId, status, previousStatus }: { mediaId: string; status: ReviewStatus; previousStatus?: ReviewStatus }) => {
  const wasArchived = previousStatus === 'ARCHIVED';
  const isArchived = status === 'ARCHIVED';
  if (isArchived && !wasArchived) client.setQueriesData(eventTablesFilter, withoutMedia(mediaId));
  if (isArchived !== wasArchived) void client.invalidateQueries({ ...eventTablesFilter, refetchType: 'all' });
};

const commentPath = (mediaId: string, commentId?: string) => `/cameras/media/${mediaId}/comment${commentId ? `/${commentId}` : ''}`;

export const mediaMutations = {
  /** Uma solicitação de vídeo; quem dispara várias em série invalida as listas no fim, pelo `mediaQueries.all()`. */
  createRequest: () =>
    mutationOptions({
      mutationFn: async ({ vehicleId, startTime, endTime, isTimelapse, enableEmailNotification, tripId }: CreateMediaRequestInput) => {
        const { createMedia } = await requestGraphQL(CreateMediaDocument, {
          createMediaRequest: {
            vehicle_id: vehicleId,
            start_time: startTime,
            end_time: endTime,
            is_timelapse: isTimelapse,
            enable_email_notification: enableEmailNotification,
            trip_id: tripId,
          },
        });
        return createMedia;
      },
    }),

  /** O link público do vídeo, com a data em que expira. */
  share: () =>
    mutationOptions({
      mutationFn: ({ mediaId }: { mediaId: string }): Promise<SharedMedia> =>
        requestHttp({ method: 'POST', host: poolerHost(), path: '/cameras/media/shared', body: { media_id: mediaId }, schema: CreateSharedMediaResponseDTO }),
    }),

  /**
   * Favorita ou desfavorita na hora, nas listas da tabela e no detalhe, e volta no erro. A resposta não é validada: a
   * tela não lê, e o que ela confirma já está no cache.
   */
  favorite: (kind: 'events' | 'requests') => {
    const listKey = [ROOT, kind === 'events' ? LIST_EVENT : LIST_REQUEST] as const;
    return mutationOptions({
      mutationFn: ({ mediaId, isFavorite }: FavoriteMediaInput) =>
        requestHttp<unknown>({ method: 'PUT', host: poolerHost(), path: `/cameras/media/${mediaId}/favorite`, body: { is_favorite: isFavorite } }),
      onMutate: async ({ mediaId, isFavorite }, { client }) => {
        const previous = await snapshot(client, [listKey, [...mediaQueries.views(), mediaId]]);
        updateMediaInPages(client, listKey, mediaId, (media) => ({ ...media, isFavorite }));
        updateViewInfo(client, mediaId, (mediaInfo) => ({ ...mediaInfo, isFavorite }));
        return previous;
      },
      onError: (_error, _input, previous, { client }) => restore(client, previous),
      // A aba de favoritas muda de conteúdo, e só ela precisa voltar ao servidor.
      onSuccess: (_data, _input, _previous, { client }) => client.invalidateQueries({ queryKey: [...listKey, { isFavorite: true }] }),
    });
  },

  /**
   * Devolve o vídeo arquivado aos Eventos, sem revisão e com motorista, ocorrências e gravidade mantidos. Sai na hora da
   * aba Arquivados; as tabelas de eventos voltam ao servidor, e o detalhe e a lista ao lado dele mostram a revisão nova.
   * O 204 (Mídia sem revisão) também é sucesso, e devolve `null`.
   */
  unarchive: () =>
    mutationOptions({
      mutationFn: async ({ mediaId }: { mediaId: string }): Promise<UnarchivedSafetyReview | null> => {
        const review = await requestHttp({
          method: 'POST',
          host: poolerHost(),
          path: `/cameras/safety-review/${mediaId}/unarchive`,
          schema: UnarchiveSafetyReviewResponse.optional(),
        });
        return review ?? null;
      },
      onSuccess: (review, { mediaId }, _result, { client }) => {
        client.setQueriesData({ queryKey: mediaQueries.eventTables(), predicate: isArchivedTab }, withoutMedia(mediaId));
        if (review) reflectUnarchive(client, mediaId, review);
        // As outras abas estão inativas e o client não refaz ao montar: sem o 'all', Favoritos voltaria sem o vídeo.
        void client.invalidateQueries({ ...eventTablesFilter, refetchType: 'all' });
      },
    }),

  /** Pede de novo um vídeo que falhou; a linha mostra o pedido na hora e volta no erro, inclusive no 412. */
  retry: () =>
    mutationOptions({
      mutationFn: async ({ mediaId }: { mediaId: string }) => {
        await requestHttp<unknown>({ method: 'PUT', host: poolerHost(), path: `/cameras/media/${mediaId}/retry` });
      },
      onMutate: async ({ mediaId }, { client }) => {
        const previous = await snapshot(client, [[ROOT, LIST_REQUEST]]);
        updateMediaInPages(client, [ROOT, LIST_REQUEST], mediaId, (media) => ({
          ...media,
          mediaDetails: { ...media.mediaDetails, updatedAt: Date.now(), requestStatus: MANUAL_RETRY_REQUEST } as MediaListItem['mediaDetails'],
        }));
        return previous;
      },
      onError: (_error, _input, previous, { client }) => restore(client, previous),
    }),

  /** O comentário entra no detalhe quando o pooler confirma, com `authorName` como autor até o detalhe voltar do janus. */
  createComment: ({ authorName }: { authorName: string }) =>
    mutationOptions({
      mutationFn: ({ mediaId, content }: MediaCommentInput): Promise<MediaComment> =>
        requestHttp({ method: 'POST', host: poolerHost(), path: commentPath(mediaId), body: { content }, schema: MediaCommentSchema }),
      onSuccess: (comment, _input, _result, { client }) => {
        const added: ViewComment = { ...comment, content: comment.content ?? undefined, updatedAt: comment.updatedAt ?? undefined, createdByUser: { fullName: authorName } };
        updateViewInfo(client, comment.mediaId, (mediaInfo) => ({ ...mediaInfo, comments: [...(mediaInfo.comments ?? []), added] }));
      },
    }),

  updateComment: () =>
    mutationOptions({
      mutationFn: ({ mediaId, commentId, content }: UpdateMediaCommentInput): Promise<MediaComment> =>
        requestHttp({ method: 'PATCH', host: poolerHost(), path: commentPath(mediaId, commentId), body: { content }, schema: MediaCommentSchema }),
      onSuccess: (comment, _input, _result, { client }) => {
        updateViewInfo(client, comment.mediaId, (mediaInfo) => ({
          ...mediaInfo,
          comments: mediaInfo.comments?.map((item) =>
            item?.id === comment.id ? { ...item, content: comment.content ?? undefined, updatedAt: comment.updatedAt ?? undefined } : item
          ),
        }));
      },
    }),

  deleteComment: () =>
    mutationOptions({
      mutationFn: async ({ mediaId, commentId }: DeleteMediaCommentInput) => {
        await requestHttp<unknown>({ method: 'DELETE', host: poolerHost(), path: commentPath(mediaId, commentId) });
      },
      onSuccess: (_data, { mediaId, commentId }, _result, { client }) => {
        updateViewInfo(client, mediaId, (mediaInfo) => ({ ...mediaInfo, comments: mediaInfo.comments?.filter((item) => item?.id !== commentId) }));
      },
    }),
};
